<!--
Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'

import usei18n from '@/lib/i18n'

import CmkButton from '@/components/CmkButton'
import CmkMultitoneIcon from '@/components/CmkIcon/CmkMultitoneIcon.vue'

import type { ConversationError } from '@/nonfree/ai/control-plane/conversation'

const props = defineProps<{
  streaming: boolean
  error: ConversationError | null
}>()

const emit = defineEmits<{
  send: [text: string]
  stop: []
}>()

const { _t } = usei18n()

const prompt = ref('')
const promptInput = useTemplateRef('promptInput')
const stopping = ref(false)

const canSend = computed(() => prompt.value.trim() !== '' && !props.streaming)

watch(
  () => [props.streaming, props.error],
  () => {
    stopping.value = false
  }
)

function insertNewline(input: HTMLTextAreaElement): void {
  input.setRangeText('\n', input.selectionStart, input.selectionEnd, 'end')
  input.dispatchEvent(new Event('input'))
}

function onPromptKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' || event.isComposing || event.shiftKey || event.altKey) {
    return
  }
  event.preventDefault()
  if (event.ctrlKey || event.metaKey) {
    insertNewline(event.target as HTMLTextAreaElement)
  } else {
    send()
  }
}

function send(): void {
  if (!canSend.value) {
    return
  }
  emit('send', prompt.value)
  prompt.value = ''
}

function stop(): void {
  stopping.value = true
  emit('stop')
}

defineExpose({ focus: () => promptInput.value?.focus() })
</script>

<template>
  <div class="ai-assistant-composer">
    <textarea
      ref="promptInput"
      v-model="prompt"
      class="ai-assistant-composer__prompt"
      rows="1"
      maxlength="10000"
      :aria-label="_t('Message')"
      :placeholder="_t('Ask across all hosts and services')"
      @keydown="onPromptKeydown"
    ></textarea>
    <CmkButton
      v-if="streaming"
      class="ai-assistant-composer__action"
      :disabled="stopping"
      @click="stop"
    >
      {{ _t('Stop') }}
    </CmkButton>
    <CmkButton
      v-else
      class="ai-assistant-composer__action ai-assistant-composer__action--icon"
      :title="_t('Send')"
      :aria-label="_t('Send')"
      :disabled="!canSend"
      @click="send"
    >
      <CmkMultitoneIcon
        class="ai-assistant-composer__icon"
        name="arrow-down"
        :primary-color="{ custom: 'var(--white)' }"
        :rotate="180"
        size="medium"
      />
    </CmkButton>
  </div>
</template>

<style scoped>
.ai-assistant-composer {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-5);
  min-height: 104px;
  max-height: 504px;
  padding: calc(var(--dimension-5) - var(--border-width-1));
  box-sizing: border-box;
  border: var(--border-width-1) solid var(--default-form-element-border-color);
  border-radius: var(--border-radius-half);
  background-color: var(--default-form-element-bg-color);
}

.ai-assistant-composer:focus-within {
  border-color: var(--ai-purple);
}

.ai-assistant-composer__prompt {
  flex: 1 1 auto;
  box-sizing: border-box;
  field-sizing: content;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  border: none;
  background: transparent;
  color: var(--font-color);
  font-family: inherit;
  font-size: var(--font-size-normal);
  outline: none;
  resize: none;

  &::placeholder {
    color: var(--font-color-secondary);
  }

  &::-webkit-scrollbar {
    width: 8px;
  }
}

.ai-assistant-composer__action {
  --default-button-optional-color: var(--background-color-ai);
  --button-optional-border-color: var(--border-color-ai);
  --button-optional-text-color: var(--white);

  align-self: flex-end;
}

.ai-assistant-composer__action--icon {
  width: var(--dimension-10);
  padding: 0;
}

.ai-assistant-composer__icon {
  flex-shrink: 0;
}
</style>
